// src/pages/LoginPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Authenticator,
  useAuthenticator,
  TextField,
  PhoneNumberField,
  PasswordField,
} from "@aws-amplify/ui-react";
import { Auth } from "aws-amplify";
import "./LoginPage.css";
import { getSubscriptionStatus } from "../../lib/subscription";


// 🔗 Public Tag Status API – backed by your tag-admin Lambda 
// REACT_APP_API_BASE=https://ku3a8yps1m.execute-api.us-east-1.amazonaws.com/default
const TAG_STATUS_API_URL = `${process.env.REACT_APP_API_BASE}/tag-status`; // ?tagId=LP1111

// LP SmartTag helpers
const TAG_ID_REGEX = /^LP\d{4}$/;
const TAG_CODE_REGEX = /^\d{5}$/; // 5-digit activation code
const PIN_REGEX = /^\d{6}$/; // 6-digit account PIN

const normalizeTagId = (value = "") => value.trim().toUpperCase();
const isValidTagId = (tagId) => TAG_ID_REGEX.test(tagId);
const isValidActivationCode = (code = "") =>
  TAG_CODE_REGEX.test(code.trim());
const isValidPin = (pin = "") => PIN_REGEX.test(String(pin).trim());

// 🔍 Helper to map backend response → "unclaimed" | "claimed" | "blocked" | "unknown"
async function fetchTagStatus(tagId) {
  if (!tagId || !isValidTagId(tagId)) return "unknown";

  try {
    const url = `${TAG_STATUS_API_URL}?tagId=${encodeURIComponent(tagId)}`;
    const resp = await fetch(url, { method: "GET" });

    if (!resp.ok) {
      console.warn("Tag status fetch failed:", resp.status);
      return "unknown";
    }

    const data = await resp.json();

    const rawStatus = (data.status || data.tagStatus || "").toLowerCase();

    if (rawStatus === "unclaimed") return "unclaimed";
    if (rawStatus === "claimed") return "claimed";
    if (rawStatus === "blocked") return "blocked";

    if (typeof data.claimed === "boolean") {
      return data.claimed ? "claimed" : "unclaimed";
    }

    return "unknown";
  } catch (err) {
    console.error("Error calling TAG_STATUS_API_URL:", err);
    return "unknown";
  }
}

// ✅ Validate SmartTag ID + password confirmation on sign up
const validateCustomSignUp = async (formData) => {
  const errors = {};

  // --- SmartTag ID (LP####) ---
  const rawTag =
    formData.tagId || formData.username || formData.preferred_username || "";
  const tag = normalizeTagId(rawTag);

  if (!isValidTagId(tag)) {
    errors.tagId =
      "Use the SmartTag ID printed on your tag (e.g., LP####). Email addresses or other IDs won’t work here.";
  } else {
    formData.username = tag;
    formData.preferred_username = tag;
    formData.tagId = tag;
  }

// --- PIN + confirm PIN match ---
const password = formData.password || "";
const confirm = formData.confirm_password || "";

if (!password) {
  errors.password = "PIN is required.";
} else if (!isValidPin(password)) {
  errors.password = "PIN must be exactly 6 digits.";
}

if (!confirm) {
  errors.confirm_password = "Please confirm your PIN.";
} else if (password && password !== confirm) {
  errors.confirm_password = "PINs do not match.";
}

  // Simple format hint for activation code (backend is the final judge)
  const rawActivation =
    formData["custom:activation_code"] || formData.activation_code || "";
  if (rawActivation && !isValidActivationCode(rawActivation)) {
    errors["custom:activation_code"] =
      "Activation code must be a 5-digit number.";
  }

  return errors;
};

// ✅ Shared Amplify Authenticator services
const services = {
  validateCustomSignUp,

  async handleSignIn({ username, password }) {
    const raw = username || "";
    const tag = normalizeTagId(raw);

    if (!isValidTagId(tag)) {
      const error = new Error(
        "Sign in with the SmartTag ID printed on your tag (e.g., LP####). Email logins are not enabled."
      );
      error.name = "InvalidSmartTagId";
      throw error;
    }

const pin = String(password || "").trim();

if (!isValidPin(pin)) {
  const error = new Error("Enter your 6-digit PIN.");
  error.name = "InvalidPin";
  throw error;
}

return Auth.signIn(tag, pin);
  },

  // ✅ Custom sign-up to inject synthetic email (SMS-only UX) + activation code
  async handleSignUp(formData) {
    const { username, password, attributes = {} } = formData;

    // Tag ID (LP####)
    const rawTag =
      formData.tagId || username || attributes.preferred_username || "";
    const tag = normalizeTagId(rawTag);

    if (!isValidTagId(tag)) {
      const err = new Error(
        "Use the SmartTag ID printed on your tag (e.g., LP####)."
      );
      err.name = "InvalidSmartTagId";
      throw err;
    }

    // 🔐 Activation code – from custom:activation_code field
    const rawActivation =
      formData["custom:activation_code"] ||
      attributes["custom:activation_code"] ||
      "";
    const activationCode = String(rawActivation).trim();

    // 📱 Phone comes from attributes.phone_number (Amplify wiring)
    const phone =
      attributes.phone_number || formData.phone_number || null;

    if (!phone) {
      const err = new Error("Mobile number is required.");
      err.name = "MissingPhoneNumber";
      throw err;
    }

    const digitsOnly = String(phone).replace(/\D/g, "");
    const syntheticEmail = `${tag.toLowerCase()}+${digitsOnly}@littlepawsplace.local`;
    const pin = String(password || "").trim();

if (!isValidPin(pin)) {
  const err = new Error("PIN must be exactly 6 digits.");
  err.name = "InvalidPin";
  throw err;
}

    return Auth.signUp({
      username: tag,
      password: pin,
      attributes: {
        ...attributes,
        preferred_username: tag,
        phone_number: phone,
        email: syntheticEmail,
        "custom:activation_code": activationCode,
      },
      autoSignIn: { enabled: true },
    });
  },
};

// 🧩 formFields: keep SmartTag wording for sign-in
const formFields = {
  signIn: {
    username: {
      label: "SmartTag ID (LP Number)",
      placeholder: "e.g., LP####",
      isRequired: true,
    },
    password: {
      label: "6-digit PIN",
      placeholder: "Enter your 6-digit PIN",
      isRequired: true,
    },
  },
  signUp: {
    phone_number: {
      required: true,
      label: "Mobile Number (SMS Required)",
    },
    preferred_username: {
      required: false,
    },
  },
};

function SignInHeader() {
  const { error } = useAuthenticator((ctx) => [ctx.error]);

  return (
    <div style={{ marginBottom: "0.75rem" }}>
      {error && (
        <div
          style={{
            backgroundColor: "#fee",
            border: "1px solid #f5c2c7",
            color: "#b00020",
            padding: "10px 12px",
            borderRadius: "6px",
            fontSize: "0.85rem",
            marginBottom: "0.5rem",
          }}
        >
          {typeof error === "string" ? error : error.message}
        </div>
      )}
    </div>
  );
}

function SignUpHeader() {
  const { error } = useAuthenticator((ctx) => [ctx.error]);

  if (!error) return null;

  const isActivationCodeError =
    error?.code === "CODE_MISSING_OR_INVALID" ||
    error?.name === "CodeMismatchException" ||
    error?.message?.includes("CODE_MISSING_OR_INVALID");

  return (
    <div style={{ marginBottom: "0.75rem" }}>
      <div
        style={{
          backgroundColor: "#fee",
          border: "1px solid #f5c2c7",
          color: "#b00020",
          padding: "10px 12px",
          borderRadius: "6px",
          fontSize: "0.85rem",
          marginBottom: "0.5rem",
        }}
      >
        {isActivationCodeError ? (
          <>
            <strong>Activation code mismatch</strong>
            <br />
            The 5-digit activation code doesn’t match this SmartTag.
            <br />
            Double-check the card in your box.
            <br />
            <a
              href="/contact-support"
              style={{ color: "#b00020", textDecoration: "underline" }}
            >
              Need help? Contact Support
            </a>
          </>
        ) : typeof error === "string" ? (
          error
        ) : (
          error.message
        )}
      </div>
    </div>
  );
}

// 🐾 Custom sign-up fields (SmartTag + activation code + phone-first)
function SignUpFields({ initialTagId = "", initialActivationCode = "" }) {
  const { validationErrors } = useAuthenticator();

  const smartTagError =
    validationErrors.tagId ||
    (validationErrors.username
      ? "Use the SmartTag ID printed on your tag (e.g., LP####)."
      : undefined);

  const activationCodeError =
    validationErrors["custom:activation_code"] ||
    validationErrors.activation_code;

  return (
    <>
      {/* Hidden username – Amplify still needs this field in the form */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        style={{
          position: "absolute",
          left: "-9999px",
          height: 0,
          width: 0,
          opacity: 0,
        }}
        tabIndex={-1}
      />

      <TextField
        name="tagId"
        label="SmartTag ID (LP Number)"
        placeholder="e.g., LP####"
        autoComplete="off"
        descriptiveText="Use the LP number printed on your SmartTag. This becomes your login ID."
        isRequired
        defaultValue={initialTagId}
        errorMessage={smartTagError}
      />

      {/* 🔐 Activation Code */}
      <TextField
        name="custom:activation_code"
        label="Activation Code"
        placeholder="5-digit code from your card"
        autoComplete="off"
        descriptiveText="Enter the 5-digit activation code printed inside your SmartTag box."
        isRequired
        defaultValue={initialActivationCode}
        errorMessage={activationCodeError}
      />

      <PhoneNumberField
        name="phone_number"
        label="Mobile Number (SMS Required)"
        placeholder="+1 555-555-1234"
        descriptiveText="We’ll use this number for secure SMS codes, SmartTag alerts, and important account messages."
        isRequired
        autoComplete="off"
        defaultDialCode="+1"
        errorMessage={validationErrors.phone_number}
      />

<PasswordField
  name="password"
  label="Create 6-digit PIN"
  placeholder="Enter 6-digit PIN"
  autoComplete="new-password"
  inputMode="numeric"
  maxLength={6}
  isRequired
  descriptiveText="Use 6 numbers. This PIN will be used with your SmartTag ID to sign in."
  errorMessage={validationErrors.password}
/>

<PasswordField
  name="confirm_password"
  label="Confirm 6-digit PIN"
  placeholder="Re-enter 6-digit PIN"
  autoComplete="new-password"
  inputMode="numeric"
  maxLength={6}
  isRequired
  errorMessage={validationErrors.confirm_password}
/>
    </>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [foundTag, setFoundTag] = useState("");
  const [foundError, setFoundError] = useState("");

  // Prefill state
  const [initialTagId, setInitialTagId] = useState("");
  const [initialActivationCode, setInitialActivationCode] = useState("");

  // Tag status + which tab to open
  const [tagStatus, setTagStatus] = useState("unknown"); // "unclaimed" | "claimed" | "blocked" | "unknown"
  const [authInitialState, setAuthInitialState] = useState("signIn");
  const [authReady, setAuthReady] = useState(false);

  // 🌐 Prefill from route + query params:
  //   /smarttag/LP1111?ac=28466
  //   /login?lp=LP1111&ac=28466
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const search = location.search || "";
      const query = new URLSearchParams(search);

      // 1️⃣ Route param wins if valid
      const routeTag = params.tagId ? normalizeTagId(params.tagId) : "";

      // 2️⃣ Fallbacks from query string
      const qpTag =
        query.get("lp") || query.get("tag") || query.get("tagId") || "";

      const chosenRaw = routeTag || qpTag || "";
      const chosenTag = normalizeTagId(chosenRaw);

      const qpAc = query.get("ac") || "";

      // Default: plain /login → signIn, no tag context
      if (!chosenTag || !isValidTagId(chosenTag)) {
        setInitialTagId("");
        setInitialActivationCode("");
        setTagStatus("unknown");
        setAuthInitialState("signIn");
        setAuthReady(true);
        return;
      }

      // Valid LP tag
      setInitialTagId(chosenTag);

      if (qpAc && isValidActivationCode(qpAc)) {
        setInitialActivationCode(qpAc.trim());
      } else {
        setInitialActivationCode("");
      }

      // 🔍 Option C: decide Sign-In vs Sign-Up based on Dynamo tag status
      try {
        const status = await fetchTagStatus(chosenTag);
        if (cancelled) return;

        setTagStatus(status);

        if (status === "unclaimed") {
          setAuthInitialState("signUp");
        } else {
          // claimed, blocked, or unknown → default to signIn
          setAuthInitialState("signIn");
        }
      } catch (err) {
        console.error("Tag status check failed:", err);
        if (!cancelled) {
          setTagStatus("unknown");
          setAuthInitialState("signIn");
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [location.search, params.tagId]);

  const handleFoundSubmit = (e) => {
    e.preventDefault();
    const tag = normalizeTagId(foundTag);

    if (!isValidTagId(tag)) {
      setFoundError("Enter the LP number from the tag, e.g., LP####.");
      return;
    }

    setFoundError("");
    navigate(`/found/${tag}`);
  };

  const SignUpFieldsWithPrefill = () => (
    <SignUpFields
      initialTagId={initialTagId}
      initialActivationCode={initialActivationCode}
    />
  );

  // Wait until we've decided whether to show Sign-In or Sign-Up initially
   // Wait until we've decided whether to show Sign-In or Sign-Up initially
  if (!authReady) {
    return (
      <div
        className="auth-wrapper"
        style={{
          display: "flex",
          justifyContent: "center",
          paddingTop: "20px",
          paddingBottom: "40px",
        }}
      >
        <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
          <div
            style={{
              marginBottom: "0.75rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              fontSize: "0.9rem",
            }}
          >
            Checking this SmartTag… 🐾
          </div>
        </div>
      </div>
    );
  }

  // Optional banner if tag appears claimed but we came from a SmartTag link
  const showClaimedBanner =
    initialTagId && tagStatus === "claimed" && authInitialState === "signIn";

  return (
    <div
      className="auth-wrapper"
      style={{
        display: "flex",
        justifyContent: "center",
        paddingTop: "20px",
        paddingBottom: "40px",
      }}
    >
      <div style={{ maxWidth: 420, width: "100%" }}>
        {/* 🐶 Finder + Support bar moved to top and tightened */}
      <div className="login-mini-links">
  <div>
    Found a pet wearing a SmartTag?{" "}
    <a href="/found" className="login-mini-link">
      Contact the owner →
    </a>
  </div>
  <div>
    Need help?{" "}
    <a href="/contact-support" className="login-mini-link">
      Contact Support
    </a>
  </div>
</div>


        {showClaimedBanner && (
          <div
            style={{
              marginBottom: "0.75rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              fontSize: "0.9rem",
            }}
          >
            <strong>This SmartTag looks already activated.</strong>
            <br />
            If you’ve already set up your account, sign in below using SmartTag{" "}
            <strong>{initialTagId}</strong>.
            <br />
            If this doesn’t look right,{" "}
            <a
              href="/contact-support"
              style={{ textDecoration: "underline" }}
            >
              contact support
            </a>{" "}
            and we can help.
          </div>
        )}

        <Authenticator
          initialState={authInitialState}
          loginMechanisms={["username"]}
          signUpAttributes={[
            "phone_number",
            "preferred_username",
            "custom:activation_code",
          ]}
          attributesToVerify={["phone_number"]}
          services={services}
          formFields={formFields}
          components={{
            SignIn: {
              Header: SignInHeader,
            },
            SignUp: {
              Header: SignUpHeader,
              FormFields: SignUpFieldsWithPrefill,
            },
          }}
        >
          <LoginRedirect navigate={navigate} />
        </Authenticator>
      </div>
    </div>
  );
}

function LoginRedirect({ navigate }) {
  const { user } = useAuthenticator();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!user) return;

      // If user is NOT subscribed, always send to /profile (your free hub)
      try {
        const res = await getSubscriptionStatus();
        if (!mounted) return;

        const isActive = res?.status === "active";
        if (!isActive) {
          navigate("/profile", { replace: true });
          return;
        }
      } catch (e) {
        // fail safe: don't create paywall loops
        if (!mounted) return;
        navigate("/profile", { replace: true });
        return;
      }

      // Subscribed users: go back to where they were trying to go (if any)
      const from = location.state?.from || "";
      if (from) {
        navigate(from, { replace: true });
      } else {
        navigate("/smarttag", { replace: true });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user, navigate, location.state]);

  return null;
}

